/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */

/**
 * Master Constants — Application-Wide Non-Domain Values
 *
 * Application-wide constants that don't fit under a business domain
 * (emails, app metadata, security policies, file types, AI config,
 * database limits, localization, error categories).
 *
 * Business-domain enums live under ./business/ and are re-exported from
 * this file for back-compat with direct master imports.
 *
 * KNOWN: BUSINESS_RULES is currently duplicated between this file and
 * business/rules.ts with drifted values (different extra fields on each
 * side). Deduping that requires pricing-trust coordination and is
 * deferred to a follow-up.
 */

// ============================================================================
// CORE APPLICATION CONSTANTS
// ============================================================================

export const EMAILS = {
  HELLO: "hello@contractor-co-pilot.com",
  CONTACT: "contact@contractor-co-pilot.com",
  SUPPORT: "support@contractor-co-pilot.com",
  HELP: "help@contractor-co-pilot.com",
  FEEDBACK: "feedback@contractor-co-pilot.com",
  NOREPLY: "noreply@contractor-co-pilot.com",
  NOTIFICATIONS: "notifications@contractor-co-pilot.com",
  BILLING: "billing@contractor-co-pilot.com",
  ACCOUNTS: "accounts@contractor-co-pilot.com",
  LEGAL: "legal@contractor-co-pilot.com",
  PRIVACY: "privacy@contractor-co-pilot.com",
  SECURITY: "security@contractor-co-pilot.com",
  DMCA: "dmca@contractor-co-pilot.com",
  ABUSE: "abuse@contractor-co-pilot.com",
  ADMIN: "admin@contractor-co-pilot.com",
  CAREERS: "careers@contractor-co-pilot.com",
  PRESS: "press@contractor-co-pilot.com",
  PARTNERS: "partners@contractor-co-pilot.com",
} as const;

export const APP_METADATA = {
  NAME: "Contractor Co-Pilot",
  VERSION: "1.0.0",
  DESCRIPTION: "AI-Powered Multi-Industry Estimation Platform",
  AUTHOR: "Contractor Co-Pilot Team",
  REPOSITORY: "https://github.com/contractor-co-pilot/platform",
  SUPPORT_EMAIL: EMAILS.SUPPORT,
  LEGAL_EMAIL: EMAILS.LEGAL,
} as const;

// ============================================================================
// BUSINESS DOMAIN CONSTANTS — RE-EXPORTS FROM ./business/
// ============================================================================

// Industries — authoritative in ./business/industries
export { INDUSTRIES, INDUSTRY_VALUES } from "./business/industries";
export type { Industry } from "./business/industries";

// User roles + hierarchy — authoritative in ./business/roles
// (master used to have a 6-role subset missing SALESMAN / CREW_LEADER / LABORER
// with drifted hierarchy values — consumers got wrong priorities depending on
// import path. Fixed by re-exporting from the business source.)
export { USER_ROLES, USER_ROLE_HIERARCHY } from "./business/roles";
export type { UserRole } from "./business/roles";

// Status enums — authoritative in ./business/statuses
export {
  CLIENT_STATUSES,
  PROJECT_STATUSES,
  ESTIMATE_STATUSES,
  PROPOSAL_STATUSES,
  BUDGET_STATUSES,
  CLIENT_STATUS_VALUES,
  PROJECT_STATUS_VALUES,
  ESTIMATE_STATUS_VALUES,
  PROPOSAL_STATUS_VALUES,
  BUDGET_STATUS_VALUES,
} from "./business/statuses";
export type {
  ClientStatus,
  ProjectStatus,
  EstimateStatus,
  ProposalStatus,
  BudgetStatus,
} from "./business/statuses";

// Industry defaults — authoritative in ./business/rules
export { INDUSTRY_CONSTANTS } from "./business/rules";

// Document types — authoritative in ./business/documents
export {
  DOCUMENT_TYPES,
  DOCUMENT_TYPE_DISPLAY_NAMES,
  DOCUMENT_TYPE_VALUES,
  SPECIALIZED_ANALYSIS_CATEGORIES,
} from "./business/documents";
export type { DocumentType } from "./business/documents";

// Template types — authoritative in ./business/templates
export {
  TEMPLATE_TYPES,
  TEMPLATE_TYPE_DISPLAY_NAMES,
  TEMPLATE_TYPE_VALUES,
} from "./business/templates";
export type { TemplateType } from "./business/templates";

// ============================================================================
// USER STATUSES (no DB enum, no external domain home)
// ============================================================================

export const USER_STATUSES = {
  ACTIVE: "ACTIVE",
  INACTIVE: "INACTIVE",
} as const;

export type UserStatus = (typeof USER_STATUSES)[keyof typeof USER_STATUSES];

export const USER_STATUS_VALUES = Object.values(USER_STATUSES);

// ============================================================================
// BUSINESS RULES — KNOWN DUPLICATE (see business/rules.ts)
// ============================================================================
// Deferred dedup — values drift between this file and business/rules.ts;
// reconciliation requires pricing-trust coordination because it affects
// DEFAULT_OVERHEAD_RATE / DEFAULT_MARGIN_RATE / DEFAULT_PROFIT_RATE /
// DEFAULT_TAX_RATE defaults used during estimate generation.

export const BUSINESS_RULES = {
  DEFAULT_OVERHEAD_RATE: 0.15,
  DEFAULT_MARGIN_RATE: 0.2,
  DEFAULT_PROFIT_RATE: 0.1,
  DEFAULT_TAX_RATE: 0.085,
  ESTIMATE_EXPIRATION_DAYS: 30,
  PROPOSAL_EXPIRATION_DAYS: 60,
  QUOTE_EXPIRATION_DAYS: 15,
  MIN_ESTIMATE_AMOUNT: 50.0,
  MAX_ESTIMATE_AMOUNT: 1000000.0,
  MIN_PROPOSAL_AMOUNT: 100.0,
  MAX_PROPOSAL_AMOUNT: 5000000.0,
  ESTIMATE_APPROVAL_THRESHOLD: 10000.0,
  PROPOSAL_APPROVAL_THRESHOLD: 50000.0,
  CONTRACT_APPROVAL_THRESHOLD: 100000.0,
  PROJECT_RETENTION_DAYS: 2555,
  DOCUMENT_RETENTION_DAYS: 2555,
  COMMUNICATION_RETENTION_DAYS: 1825,
  MIN_CONTRACT_AMOUNT: 500.0,
  MAX_CONTRACT_AMOUNT: 10000000.0,
  ESTIMATE_EXPIRATION_WARNING_HOURS: 72,
  PROPOSAL_EXPIRATION_WARNING_HOURS: 168,
  FOLLOW_UP_REMINDER_HOURS: 48,
  BUDGET_VARIANCE_WARNING_THRESHOLD: 0.15,
  BUDGET_VARIANCE_ERROR_THRESHOLD: 0.2,
} as const;

// ============================================================================
// COMMUNICATION
// ============================================================================

export const COMMUNICATION_TYPES = {
  EMAIL: "EMAIL",
  PHONE_CALL: "PHONE_CALL",
  MEETING: "MEETING",
  NOTE: "NOTE",
  SMS: "SMS",
} as const;

export type CommunicationType =
  (typeof COMMUNICATION_TYPES)[keyof typeof COMMUNICATION_TYPES];

// ============================================================================
// SECURITY CONSTANTS
// ============================================================================

export const SECURITY_POLICIES = {
  SESSION_TIMEOUT_MINUTES: 480,
  SESSION_EXTEND_ON_ACTIVITY: true,
  MAX_CONCURRENT_SESSIONS: 5,
  LOGIN_ATTEMPTS_MAX: 5,
  LOGIN_ATTEMPTS_WINDOW_MINUTES: 15,
  LOGIN_LOCKOUT_DURATION_MINUTES: 30,
  PASSWORD_MIN_LENGTH: 8,
  PASSWORD_MAX_LENGTH: 128,
  PASSWORD_REQUIRE_UPPERCASE: true,
  PASSWORD_REQUIRE_LOWERCASE: true,
  PASSWORD_REQUIRE_NUMBERS: true,
  PASSWORD_REQUIRE_SPECIAL_CHARS: true,
  MFA_REQUIRED_FOR_ADMIN: true,
  MFA_REQUIRED_FOR_FINANCIAL: true,
  AUDIT_LOG_RETENTION_DAYS: 2555,
  API_KEY_ROTATION_DAYS: 90,
  FILE_SIZE_LIMIT_BYTES: 10485760,
  ALLOWED_FILE_EXTENSIONS: [
    ".pdf",
    ".jpg",
    ".jpeg",
    ".png",
    ".xlsx",
    ".xls",
    ".csv",
  ],
} as const;

// ============================================================================
// FILE AND MEDIA CONSTANTS
// ============================================================================

export const FILE_TYPES = {
  PDF: "application/pdf",
  JPEG: "image/jpeg",
  PNG: "image/png",
  GIF: "image/gif",
  WEBP: "image/webp",
  EXCEL: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  EXCEL_LEGACY: "application/vnd.ms-excel",
  CSV: "text/csv",
  JSON: "application/json",
  DOCX: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  DOC: "application/msword",
  TXT: "text/plain",
  ZIP: "application/zip",
  RAR: "application/x-rar-compressed",
  DWG: "application/acad",
  DXF: "application/dxf",
} as const;

export type FileType = (typeof FILE_TYPES)[keyof typeof FILE_TYPES];

export const FILE_EXTENSIONS: Record<FileType, string[]> = {
  [FILE_TYPES.PDF]: [".pdf"],
  [FILE_TYPES.JPEG]: [".jpg", ".jpeg"],
  [FILE_TYPES.PNG]: [".png"],
  [FILE_TYPES.GIF]: [".gif"],
  [FILE_TYPES.WEBP]: [".webp"],
  [FILE_TYPES.EXCEL]: [".xlsx"],
  [FILE_TYPES.EXCEL_LEGACY]: [".xls"],
  [FILE_TYPES.CSV]: [".csv"],
  [FILE_TYPES.JSON]: [".json"],
  [FILE_TYPES.DOCX]: [".docx"],
  [FILE_TYPES.DOC]: [".doc"],
  [FILE_TYPES.TXT]: [".txt"],
  [FILE_TYPES.ZIP]: [".zip"],
  [FILE_TYPES.RAR]: [".rar"],
  [FILE_TYPES.DWG]: [".dwg"],
  [FILE_TYPES.DXF]: [".dxf"],
} as const;

// ============================================================================
// AI & DATABASE CONFIGURATION
// ============================================================================

export const AI_CONFIG = {
  DEFAULT_MODEL: "gpt-4o",
  MAX_TOKENS: 4000,
  TEMPERATURE: 0.7,
  TOP_P: 0.9,
  FREQUENCY_PENALTY: 0.0,
  PRESENCE_PENALTY: 0.0,
  MAX_RETRIES: 3,
  TIMEOUT_MS: 60000,
} as const;

export const DATABASE_LIMITS = {
  MAX_ROWS_PER_QUERY: 10000,
  MAX_ROWS_PER_PAGE: 1000,
  DEFAULT_PAGE_SIZE: 20,
  MAX_BATCH_SIZE: 1000,
  MAX_STRING_LENGTH: 65535,
  MAX_TEXT_LENGTH: 1048576,
  MAX_EXPORT_ROWS: 10000,
} as const;

// ============================================================================
// LOCALIZATION CONSTANTS
// ============================================================================

export const CURRENCIES = {
  USD: "USD",
  CAD: "CAD",
  EUR: "EUR",
  GBP: "GBP",
  AUD: "AUD",
  JPY: "JPY",
  CHF: "CHF",
  CNY: "CNY",
  INR: "INR",
  MXN: "MXN",
} as const;

export type Currency = (typeof CURRENCIES)[keyof typeof CURRENCIES];

export const TIME_ZONES = {
  UTC: "UTC",
  EST: "America/New_York",
  CST: "America/Chicago",
  MST: "America/Denver",
  PST: "America/Los_Angeles",
  GMT: "Europe/London",
  CET: "Europe/Paris",
  JST: "Asia/Tokyo",
  AEST: "Australia/Sydney",
} as const;

export type TimeZone = (typeof TIME_ZONES)[keyof typeof TIME_ZONES];

// ============================================================================
// ERROR CATEGORIES
// ============================================================================

export const ERROR_SEVERITY = {
  LOW: "low",
  MEDIUM: "medium",
  HIGH: "high",
  CRITICAL: "critical",
} as const;

export const ERROR_CATEGORIES = {
  AUTHENTICATION: "authentication",
  AUTHORIZATION: "authorization",
  VALIDATION: "validation",
  NOT_FOUND: "not_found",
  CONFLICT: "conflict",
  RATE_LIMIT: "rate_limit",
  DATABASE: "database",
  SYSTEM: "system",
} as const;
